import React, { useState, useEffect } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSelectedCity } from '../contexts/SelectedCityContext';
import {
  downloadReport, filtrosDoRelatorio, reportsForRole, FORMATOS, GRUPOS, ROTULO_FILTRO,
} from '../services/reportService';
import { getAllSchools, getAllClasses } from '../services/dashboardService';
import SearchableSelect from '../components/SearchableSelect';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import { STATUS_ALUNO } from '../utils/statusAluno';
import { fetchAllPaginated } from '../services/api';
import { BotoesDeDocumentos } from '../components/DocumentosDoAluno';
import { baixarFichaEmBranco, documentosPara } from '../services/documentosAluno';

function Relatorios() {
  const { user, hasRole } = useAuth();
  const { darkMode } = useTheme();

  // Cidade vem do sidebar (SelectedCityContext)
  const { cities, selectedCityId } = useSelectedCity();
  const selectedCity = selectedCityId ? String(selectedCityId) : '';
  const selectedCityObj = cities.find((c) => Number(c.id) === Number(selectedCityId));

  // Estados para filtros
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);

  // Declarações do aluno: alunos da turma escolhida nos filtros
  const [alunosDaTurma, setAlunosDaTurma] = useState([]);
  const [alunoDoc, setAlunoDoc] = useState('');
  const [carregandoAlunos, setCarregandoAlunos] = useState(false);
  const [gerandoFicha, setGerandoFicha] = useState(false);

  const baixarFichaVazia = async () => {
    setGerandoFicha(true);
    setErrorMessage('');
    try {
      await baixarFichaEmBranco(selectedSchool || null);
    } catch (err) {
      setErrorMessage(err.message || 'Não foi possível gerar a ficha.');
    } finally {
      setGerandoFicha(false);
    }
  };

  useEffect(() => {
    setAlunoDoc('');
    setAlunosDaTurma([]);
    if (!selectedClass || documentosPara(user).length === 0) return undefined;
    let cancelado = false;
    setCarregandoAlunos(true);
    fetchAllPaginated('/students/', { id_class: selectedClass })
      .then((lista) => {
        if (cancelado) return;
        setAlunosDaTurma([...(lista || [])].sort((a, b) =>
          (a.student_name || '').localeCompare(b.student_name || '', 'pt-BR')));
      })
      .catch(() => { if (!cancelado) setErrorMessage('Não foi possível carregar os alunos da turma.'); })
      .finally(() => { if (!cancelado) setCarregandoAlunos(false); });
    return () => { cancelado = true; };
  }, [selectedClass, user]);

  // Estados de loading
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [exportingReport, setExportingReport] = useState(null);

  // Estados para mensagens
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Verifica se o usuário é admin sem cidade (pode filtrar por cidade)
  const isAdminWithoutCity = hasRole(ROLES.ADMIN) && !user?.city;
  const isGerente = hasRole(ROLES.SUPERVISOR);

  // Anos disponiveis para filtro
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Meses disponiveis
  const months = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  // Status de alunos. 'graduated' e 'dropped' ficavam aqui, mas nenhuma tela
  // os grava: filtrar por eles devolvia relatório vazio.
  const studentStatuses = STATUS_ALUNO;

  // Carrega cidades e escolas
  useEffect(() => {
    const loadFilters = async () => {
      setLoadingFilters(true);
      try {
        // Cidades ja vem do SelectedCityContext (sidebar)

        // Carrega escolas e turmas
        const [schoolsData, classesData] = await Promise.all([
          getAllSchools(user?.city || null),
          getAllClasses(user?.city || null),
        ]);
        setSchools(schoolsData);
        setClasses(classesData);

        // Para gerentes, filtra apenas escolas atribuidas
        if (isGerente && user?.assigned_school_ids) {
          const assignedSchools = schoolsData.filter(s =>
            user.assigned_school_ids.includes(s.id)
          );
          setFilteredSchools(assignedSchools);
          setFilteredClasses(classesData.filter(c =>
            user.assigned_school_ids.includes(c.id_school)
          ));
        } else {
          setFilteredSchools(schoolsData);
          setFilteredClasses(classesData);
        }
      } catch (error) {
        console.error('Erro ao carregar filtros:', error);
        setErrorMessage('Erro ao carregar filtros. Tente novamente.');
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, [isAdminWithoutCity, isGerente, user?.city, user?.assigned_school_ids]);

  // Filtra escolas e turmas quando cidade muda (para admin)
  useEffect(() => {
    if (isAdminWithoutCity && selectedCity) {
      const citySchools = schools.filter(s => s.id_city === parseInt(selectedCity));
      setFilteredSchools(citySchools);
      const citySchoolIds = citySchools.map(s => s.id);
      setFilteredClasses(classes.filter(c => citySchoolIds.includes(c.id_school)));
      setSelectedSchool('');
      setSelectedClass('');
    } else if (isAdminWithoutCity && !selectedCity) {
      setFilteredSchools(schools);
      setFilteredClasses(classes);
    }
  }, [selectedCity, schools, classes, isAdminWithoutCity]);

  // Filtra turmas quando escola muda
  useEffect(() => {
    if (selectedSchool) {
      setFilteredClasses(classes.filter(c => c.id_school === parseInt(selectedSchool)));
      setSelectedClass('');
    } else if (!isAdminWithoutCity || !selectedCity) {
      setFilteredClasses(classes);
    } else if (selectedCity) {
      const citySchoolIds = schools.filter(s => s.id_city === parseInt(selectedCity)).map(s => s.id);
      setFilteredClasses(classes.filter(c => citySchoolIds.includes(c.id_school)));
    }
  }, [selectedSchool, classes, schools, selectedCity, isAdminWithoutCity]);

  // Limpa mensagens apos 5 segundos
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Exporta um relatório no formato escolhido (xlsx, pdf ou csv)
  const handleExport = async (reportType, formato) => {
    setExportingReport(`${reportType.id}:${formato}`);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const filters = filtrosDoRelatorio(reportType, {
        city: isAdminWithoutCity ? selectedCity : '',
        school: selectedSchool,
        classId: selectedClass,
        year: selectedYear,
        month: selectedMonth,
        status: selectedStatus,
      });

      // Partes descritivas do nome do arquivo (cidade, escola, turma,
      // período) — o arquivo baixado já vem identificado, ex.:
      // "alunos_Escola_Municipal_Sao_Jose_5_Ano_A_2025-08-16.xlsx".
      const schoolObj = filteredSchools.find(s => String(s.id) === String(selectedSchool));
      const classObj = filteredClasses.find(c => String(c.id) === String(selectedClass));
      const filenameParts = [];
      if (isAdminWithoutCity && selectedCityObj) filenameParts.push(selectedCityObj.city);
      if (schoolObj) filenameParts.push(schoolObj.school);
      if (filters.class_id && classObj) filenameParts.push(classObj.class_name);
      if (filters.year_month) filenameParts.push(filters.year_month);
      else if (filters.year) filenameParts.push(String(filters.year));

      await downloadReport(reportType, filters, formato, { filenameParts });
      const rotulo = FORMATOS.find(f => f.id === formato)?.label || formato;
      setSuccessMessage(`Relatório "${reportType.title}" exportado em ${rotulo}.`);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      if (error.status === 403) {
        setErrorMessage('Você não tem permissão para exportar este relatório.');
      } else {
        setErrorMessage(`Erro ao exportar relatório: ${error.message}`);
      }
    } finally {
      setExportingReport(null);
    }
  };

  // Limpa todos os filtros (exceto cidade, que eh controlada pelo sidebar)
  const clearFilters = () => {
    setSelectedSchool('');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedStatus('');
    setSelectedClass('');
  };

  // Relatorios disponiveis para o perfil. O Professor exporta o que é das
  // turmas dele; os relatórios de gestão não aparecem para ele.
  const reports = reportsForRole(user?.role);
  const grupos = GRUPOS
    .map(g => ({ ...g, reports: reports.filter(r => r.grupo === g.id) }))
    .filter(g => g.reports.length > 0);

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
          }`}>
            Relatórios
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Exporte os dados do sistema em Excel, PDF ou CSV
          </p>
        </div>
      </header>

      {/* Mensagens de feedback */}
      {successMessage && (
        <div className="mb-6 p-4 rounded-lg bg-[#13983D]/10 dark:bg-[#13983D]/20 text-[#13983D] dark:text-[#13983D]/80 border border-[#13983D]/20 dark:border-[#13983D]/30">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-6 p-4 rounded-lg bg-[#E83A3C]/10 dark:bg-[#E83A3C]/20 text-[#E83A3C] dark:text-[#E83A3C]/80 border border-[#E83A3C]/20 dark:border-[#E83A3C]/30">
          {errorMessage}
        </div>
      )}

      {/* Informacao de acesso */}
      <div className={`mb-6 p-4 rounded-lg ${darkMode
        ? 'bg-[#0974BA]/20 border border-[#0974BA]/30'
        : 'bg-[#0974BA]/5 border border-[#0974BA]/20'
      }`}>
        <p className={`text-sm ${darkMode ? 'text-[#0974BA]/80' : 'text-[#0974BA]'}`}>
          {isAdminWithoutCity && (
            <>Você tem acesso a dados de todas as cidades. Use o filtro de cidade para restringir os dados.</>
          )}
          {hasRole(ROLES.COORDENADOR) && user?.city_name && (
            <>Você tem acesso aos dados da cidade de <strong>{user.city_name}</strong>.</>
          )}
          {isGerente && (
            <>Você tem acesso apenas aos dados das escolas atribuídas a você.</>
          )}
          {hasRole(ROLES.GESTOR) && user?.city_name && (
            <>Você tem acesso somente leitura aos dados da cidade de <strong>{user.city_name}</strong>.</>
          )}
          {hasRole(ROLES.PROFESSOR) && (
            <>Os relatórios trazem apenas as turmas em que você leciona.</>
          )}
        </p>
      </div>

      {/* Filtros */}
      <div className={`mb-8 p-6 rounded-xl border ${darkMode
        ? 'bg-slate-800/70 border-slate-700/50'
        : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          Filtros
        </h2>

        {loadingFilters ? (
          <LoadingSpinnerAdvanced
            darkMode={darkMode}
            message="Carregando filtros..."
            showSubtitle={false}
            size="sm"
            fullHeight={false}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtro de Cidade (apenas para admin sem cidade) — definido no sidebar */}
            {isAdminWithoutCity && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Cidade
                </label>
                <div
                  className={`w-full px-3 py-2 rounded-lg border flex items-center gap-2 ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-300'
                    : 'bg-slate-50 border-slate-200 text-slate-600'
                  }`}
                  title="Altere a cidade no menu lateral"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate text-sm">
                    {selectedCityObj ? selectedCityObj.city : 'Todas as cidades'}
                  </span>
                </div>
              </div>
            )}

            {/* Filtro de Escola */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Escola
              </label>
              <SearchableSelect
                value={selectedSchool}
                onChange={setSelectedSchool}
                options={filteredSchools.map(s => ({ value: s.id, label: s.school }))}
                emptyOptionLabel="Todas as escolas"
                darkMode={darkMode}
                className="w-full"
              />
            </div>

            {/* Filtro de Turma */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Turma
              </label>
              <SearchableSelect
                value={selectedClass}
                onChange={setSelectedClass}
                options={filteredClasses.map(cls => ({
                  value: cls.id,
                  label: `${cls.class_name} - ${cls.grade} (${cls.shift})`,
                }))}
                emptyOptionLabel="Todas as turmas"
                darkMode={darkMode}
                className="w-full"
              />
            </div>

            {/* Filtro de Ano */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Ano
              </label>
              <SearchableSelect
                value={selectedYear}
                onChange={(ano) => {
                  setSelectedYear(ano);
                  // Mês sem ano não filtra nada: some junto com o ano.
                  if (!ano) setSelectedMonth('');
                }}
                options={years}
                emptyOptionLabel="Todos os anos"
                darkMode={darkMode}
                className="w-full"
              />
            </div>

            {/* Filtro de Mes */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Mês
              </label>
              <SearchableSelect
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={months}
                emptyOptionLabel={selectedYear ? 'Todos os meses' : 'Escolha o ano primeiro'}
                disabled={!selectedYear}
                darkMode={darkMode}
                className="w-full"
              />
            </div>

            {/* Filtro de Status (para alunos) */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Situação do Aluno
              </label>
              <SearchableSelect
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={studentStatuses}
                emptyOptionLabel="Todas as situações"
                darkMode={darkMode}
                className="w-full"
              />
            </div>

            {/* Botao limpar filtros */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${darkMode
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                }`}
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Declarações do aluno — só a gestão emite */}
      {documentosPara(user).length > 0 && (
        <section className={`mb-8 p-6 rounded-xl border ${darkMode
          ? 'bg-slate-800/70 border-slate-700/50'
          : 'bg-white border-slate-200 shadow-sm'
        }`}>
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Declarações do Aluno
          </h2>
          <p className={`text-sm mt-1 mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Escolha a escola e a turma nos filtros acima, depois o aluno. Os documentos saem em PDF,
            com o cabeçalho da prefeitura e o que o cadastro do aluno tiver preenchido.
          </p>

          {/* Ficha em branco: para a matrícula feita no papel, antes do cadastro */}
          <div className={`flex flex-wrap items-center justify-between gap-3 p-3 mb-5 rounded-lg border ${darkMode
            ? 'border-slate-700 bg-slate-900/30'
            : 'border-slate-200 bg-slate-50'
          }`}>
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                Ficha de Matrícula em branco
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {selectedSchool
                  ? `Já com os dados de ${filteredSchools.find((e) => String(e.id) === String(selectedSchool))?.school || 'escola escolhida'}.`
                  : 'Escolha a escola nos filtros para ela já sair preenchida na ficha.'}
              </p>
            </div>
            <button
              type="button"
              onClick={baixarFichaVazia}
              disabled={gerandoFicha}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0974BA] hover:bg-[#0974BA]/85 text-white disabled:opacity-60"
            >
              {gerandoFicha ? 'Gerando...' : 'Baixar ficha em branco'}
            </button>
          </div>

          {!selectedClass ? (
            <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Para os documentos de um aluno, escolha a turma nos filtros.
            </p>
          ) : (
            <>
              <div className="max-w-xl mb-4">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Aluno
                </label>
                <SearchableSelect
                  value={alunoDoc}
                  onChange={setAlunoDoc}
                  options={alunosDaTurma.map((a) => ({ value: a.id_student, label: a.student_name }))}
                  emptyOptionLabel={carregandoAlunos ? 'Carregando alunos...' : 'Escolha o aluno'}
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>
              <BotoesDeDocumentos
                aluno={alunosDaTurma.find((a) => String(a.id_student) === String(alunoDoc))}
                documentos={documentosPara(user)}
                darkMode={darkMode}
                onErro={(msg) => setErrorMessage(msg || '')}
              />
            </>
          )}
        </section>
      )}

      {/* Relatórios, por assunto */}
      <div className="space-y-8">
        {grupos.map((grupo) => (
          <section key={grupo.id}>
            <h2 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {grupo.titulo}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grupo.reports.map((report) => (
                <div
                  key={report.id}
                  className={`rounded-xl p-5 border transition-colors flex flex-col ${darkMode
                    ? 'bg-slate-800/70 border-slate-700/10 hover:border-sideb-blue/50'
                    : 'bg-white/90 border-slate-200 shadow-sm hover:border-[#0974BA]/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-lg flex-shrink-0 flex items-center justify-center text-xl ${darkMode
                      ? 'bg-sideb-blue/20 text-sideb-blue'
                      : 'bg-[#0974BA]/10 text-[#0974BA]'
                    }`}>
                      {report.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-base ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        {report.title}
                      </h3>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {report.description}
                      </p>
                      <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        Filtros: {report.filters.map(f => ROTULO_FILTRO[f]).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200/60 dark:border-slate-700/50">
                    {FORMATOS.map((formato) => {
                      const chave = `${report.id}:${formato.id}`;
                      const exportando = exportingReport === chave;
                      const cor = {
                        xlsx: 'bg-sideb-green hover:bg-sideb-green/80 text-white',
                        pdf: 'bg-[#E83A3C] hover:bg-[#E83A3C]/80 text-white',
                        csv: darkMode
                          ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                          : 'bg-slate-200 hover:bg-slate-300 text-slate-700',
                      }[formato.id];
                      return (
                        <button
                          key={formato.id}
                          onClick={() => handleExport(report, formato.id)}
                          disabled={exportingReport !== null}
                          title={`Baixar ${report.title} em ${formato.label}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50 ${cor} ${exportando ? 'cursor-wait' : ''}`}
                        >
                          {exportando ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          )}
                          <span>{exportando ? 'Gerando...' : formato.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <p className={`mt-8 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        O Excel já vem com as colunas separadas, cabeçalho fixo e filtro ligado. O CSV usa ponto
        e vírgula, como o Excel em português espera. Filtros que não se aplicam a um relatório
        são ignorados nele.
      </p>
    </>
  );
}

export default Relatorios;
